import React, { useRef, useState, Suspense, useEffect } from 'react';
import { Canvas, useLoader, useFrame } from '@react-three/fiber';
import { TextureLoader } from 'three';
import { Html, OrbitControls, Stars } from '@react-three/drei';
import * as THREE from 'three';
import { Frame } from '@react95/core';

const Book = ({ backgroundMusicRef, onVideoStateChange }) => {
  const frontTexture = useLoader(TextureLoader, '/images/Front.png');
  const backTexture = useLoader(TextureLoader, '/images/Back.png');
  const pageTexture = useLoader(TextureLoader, '/images/Page.png');
  
  // Load all polaroid textures
  const polaroid1 = useLoader(TextureLoader, '/images/RicePolaroid1.png');
  const polaroid2 = useLoader(TextureLoader, '/images/RicePolaroid2.png');
  const polaroid3 = useLoader(TextureLoader, '/images/RicePolaroid3.png');
  const polaroid4 = useLoader(TextureLoader, '/images/RicePolaroid4.png');
  const polaroid5 = useLoader(TextureLoader, '/images/RicePolaroid5.png');
  const polaroid6 = useLoader(TextureLoader, '/images/RicePolaroid6.png');
  const polaroid7 = useLoader(TextureLoader, '/images/RicePolaroid7.png');
  const polaroid8 = useLoader(TextureLoader, '/images/RicePolaroid8.png');
  const polaroid9 = useLoader(TextureLoader, '/images/RicePolaroid9.png');
  const polaroid10 = useLoader(TextureLoader, '/images/RicePolaroid10.png');
  const polaroid11 = useLoader(TextureLoader, '/images/RicePolaroid11.png');
  
  const bookRef = useRef();
  const [currentPage, setCurrentPage] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);
  
  const videoRef = useRef(null);
  const [isVideoPage, setIsVideoPage] = useState(false);
  
  // Add new state for zoom animation
  const [targetZoom, setTargetZoom] = useState(1.8);
  const [currentZoom, setCurrentZoom] = useState(1.8);

  const pages = [
    { content: "" },  // Empty content for first page (behind front cover)
    { content: "", texture: polaroid1 },
    { 
      type: 'video',
      videoSrc: '/videos/How Minecraft Got Me Into College.mp4'
    },
    { content: "", texture: polaroid2 },
    { 
      type: 'video',
      videoSrc: '/videos/We\'re Saving TikTok.mp4'
    },
    { content: "", texture: polaroid3 },
    { 
      type: 'video',
      videoSrc: '/videos/Introducing Out.so.mp4'
    },
    { content: "", texture: polaroid4 },
    { 
      type: 'video',
      videoSrc: '/videos/This Bot Will Destroy Minecraft.mp4'
    },
    { content: "", texture: polaroid5 },
    { 
      type: 'video',
      videoSrc: '/videos/Banned For Saving Minecraft\'s Forgotten Gamemode.mp4'
    },
    { content: "", texture: polaroid6 },
    { 
      type: 'video',
      videoSrc: '/videos/Reupload with Out.so.mp4'
    },
    { content: "", texture: polaroid7 },
    { 
      type: 'video',
      videoSrc: '/videos/you\'re too late.mp4'
    },
    { content: "", texture: polaroid8 },
    { 
      type: 'video',
      videoSrc: '/videos/Introducing Screate.mp4'
    },
    { content: "", texture: polaroid9 },
    { content: "", texture: polaroid10 },
    { content: "", texture: polaroid11 }
  ];

  useEffect(() => {
    // Handle audio transitions when entering/leaving video page
    if (isVideoPage) {
      let bgVolume = backgroundMusicRef.current.volume;
      let videoVolume = 0;
      
      // Fade out background music and fade in video
      const fadeTransition = setInterval(() => {
        // Decrease background music
        if (bgVolume > 0) {
          bgVolume = Math.max(0, bgVolume - 0.02);
          backgroundMusicRef.current.volume = bgVolume;
        } else {
          backgroundMusicRef.current.pause();
        }

        // Increase video volume
        if (videoRef.current && videoVolume < 1) {
          videoVolume = Math.min(1, videoVolume + 0.02);
          videoRef.current.volume = videoVolume;
          if (!videoRef.current.playing) {
            videoRef.current.play();
          }
        }

        // Stop interval when both transitions are complete
        if (bgVolume === 0 && videoVolume >= 1) {
          clearInterval(fadeTransition);
        }
      }, 50);

      return () => clearInterval(fadeTransition);
    } else {
      let bgVolume = 0;
      let videoVolume = videoRef.current ? videoRef.current.volume : 0;
      
      // Fade out video and fade in background music
      const fadeTransition = setInterval(() => {
        // Decrease video volume
        if (videoRef.current && videoVolume > 0) {
          videoVolume = Math.max(0, videoVolume - 0.02);
          videoRef.current.volume = videoVolume;
          if (videoVolume === 0) {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
          }
        }

        // Increase background music
        if (bgVolume < 0.5) {
          bgVolume = Math.min(0.5, bgVolume + 0.02);
          backgroundMusicRef.current.volume = bgVolume;
          if (!backgroundMusicRef.current.playing) {
            backgroundMusicRef.current.play();
          }
        }

        // Stop interval when both transitions are complete
        if (videoVolume === 0 && bgVolume >= 0.5) {
          clearInterval(fadeTransition);
        }
      }, 50);

      return () => clearInterval(fadeTransition);
    }
  }, [isVideoPage, backgroundMusicRef]);

  const handlePageChange = (newPage) => {
    const isVideoPage = pages[newPage]?.type === 'video';
    setIsVideoPage(isVideoPage);
    onVideoStateChange(isVideoPage);
    setCurrentPage(newPage);
    setTargetZoom(isVideoPage ? 4 : 1.8);
  };

  const handleNextPage = () => {
    if (!isFlipping && currentPage < pages.length - 1) {
      setIsFlipping(true);
      handlePageChange(currentPage + 1);
      setTimeout(() => setIsFlipping(false), 500);
    }
  };

  const handlePrevPage = () => {
    if (!isFlipping && currentPage > 0) {
      setIsFlipping(true);
      handlePageChange(currentPage - 1);
      setTimeout(() => setIsFlipping(false), 500);
    }
  };

  useFrame((state) => {
    if (bookRef.current) {
      // Keep existing book animation and user control
      bookRef.current.rotation.y = Math.sin(state.clock.getElapsedTime() * 0.3) * 0.1;
      
      // Smooth zoom animation
      const zoomSpeed = 0.05;
      const newZoom = THREE.MathUtils.lerp(currentZoom, targetZoom, zoomSpeed);
      setCurrentZoom(newZoom);
      
      // Apply zoom to camera while preserving its current orientation
      const currentPosition = state.camera.position.clone();
      currentPosition.normalize().multiplyScalar(10 / newZoom);
      state.camera.position.copy(currentPosition);
      state.camera.updateProjectionMatrix();
    }
  });

  return (
    <group ref={bookRef}>
      <group position={[0, 0, 0]} rotation={[0.2, 0, 0]}>
        {/* Front Cover */}
        {currentPage === 0 && (
          <mesh position={[0, 0, 0.1]} renderOrder={2}>
            <planeGeometry args={[4.2, 6.2]} />
            <meshStandardMaterial 
              map={frontTexture}
              side={THREE.DoubleSide}
              transparent={true}
              depthWrite={true}
              depthTest={true}
            />
          </mesh>
        )}

        {/* Pages */}
        {pages.map((page, index) => (
          <group 
            key={index} 
            position={[
              index < currentPage ? -3.8 : 0,
              0,
              0.05 - (index * 0.01)
            ]}
            rotation={[0, index < currentPage ? Math.PI : 0, 0]}
          >
            {/* Base page */}
            <mesh renderOrder={1}>
              <planeGeometry args={[4, 6]} />
              <meshStandardMaterial 
                map={pageTexture} 
                side={THREE.DoubleSide}
                transparent={true}
                depthWrite={true}
                depthTest={true}
              />
            </mesh>
            {index > 0 && (
              <>
                {/* Current page content */}
                {index === currentPage && (
                  <group>
                    {page.type === 'video' ? (
                      <>
                        {/* Video caption */}
                        <Html
                          transform
                          position={[1.0, .8, 0.001]}
                          style={{
                            width: '120px',
                            height: 'auto',
                            display: index === currentPage ? 'block' : 'none',
                            pointerEvents: 'none',
                            transform: 'translate(-50%, -50%)',
                            opacity: isVideoPage ? 1 : 0,
                            transition: 'opacity 0.5s ease',
                            textAlign: 'center',
                            color: 'black',
                            fontSize: '10px',
                            fontFamily: 'OMORI_GAME, monospace',
                            userSelect: 'none',
                            WebkitUserSelect: 'none',
                            MozUserSelect: 'none',
                            msUserSelect: 'none'
                          }}
                          occlude
                          prepend
                        >
                          {page.videoSrc.includes('Minecraft Got Me') ? 'How Minecraft Got Me Into College' :
                           page.videoSrc.includes('TikTok') ? 'We\'re Saving TikTok' :
                           page.videoSrc.includes('Out.so') && !page.videoSrc.includes('Reupload') ? 'Introducing Out.so' :
                           page.videoSrc.includes('Bot Will') ? 'This Bot Will Destroy Minecraft' :
                           page.videoSrc.includes('Reupload') ? 'Reupload with Out.so' :
                           page.videoSrc.includes('too late') ? 'you\'re too late.' :
                           page.videoSrc.includes('Screate') ? 'Introducing Screate' :
                           'Banned For Saving Minecraft\'s Forgotten Gamemode'}
                        </Html>

                        {/* Existing video element */}
                        <Html
                          transform
                          position={[1.0, -1, 0.001]}
                          style={{
                            width: '120px',
                            height: '67.5px',
                            display: index === currentPage ? 'block' : 'none',
                            pointerEvents: 'none',
                            transform: 'translate(-50%, -50%)'
                          }}
                          occlude
                          prepend
                        >
                          <video
                            ref={videoRef}
                            src={page.videoSrc}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                            autoPlay
                            muted={false}
                            loop={false}
                            playsInline
                          />
                        </Html>
                      </>
                    ) : (
                      <>
                        <Html
                          transform
                          position={[0, 1.5, 0.001]}
                          style={{
                            fontSize: '20px',
                            color: 'black',
                            background: 'transparent',
                            padding: '20px',
                            textAlign: 'center',
                            width: '100%',
                            pointerEvents: 'none',
                            userSelect: 'none',
                            fontFamily: 'OMORI_GAME, monospace',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            display: index === currentPage ? 'block' : 'none'
                          }}
                        >
                          {page.content}
                        </Html>
                        {page.texture && (
                          <mesh 
                            position={[0, 0, 0.002]} 
                            renderOrder={2}
                          >
                            <planeGeometry args={[2, 2.4]} />
                            <meshStandardMaterial
                              map={page.texture}
                              side={THREE.FrontSide}
                              transparent={true}
                              depthWrite={false}
                              depthTest={true}
                            />
                          </mesh>
                        )}
                      </>
                    )}
                  </group>
                )}

                {/* Previous page content (left side) */}
                {index === currentPage - 1 && (
                  <group rotation={[0, -Math.PI, 0]}>
                    {page.type === 'video' ? (
                      <Html
                        transform
                        position={[.9, -1, -0.001]}
                        style={{
                          width: '120px',
                          height: '67.5px',
                          pointerEvents: 'none',
                          transform: 'translate(-50%, -50%)'
                        }}
                        renderOrder={-1}
                      >
                        <video
                          src={page.videoSrc}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                          playsInline
                          muted
                          onLoadedData={(e) => {
                            e.target.currentTime = 5;
                            e.target.pause();
                          }}
                        />
                      </Html>
                    ) : (
                      <>
                        <Html
                          transform
                          position={[0, 1.5, -0.001]}
                          style={{
                            fontSize: '20px',
                            color: 'black',
                            background: 'transparent',
                            padding: '20px',
                            textAlign: 'center',
                            width: '100%',
                            pointerEvents: 'none',
                            userSelect: 'none',
                            fontFamily: 'OMORI_GAME, monospace',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            display: index === currentPage - 1 ? 'block' : 'none'
                          }}
                        >
                          {page.content}
                        </Html>
                        <mesh 
                          position={[0, 0, -0.002]} 
                          renderOrder={999}
                        >
                          <planeGeometry args={[2, 2.4]} />
                          <meshStandardMaterial
                            map={page.texture}
                            side={THREE.FrontSide}
                            transparent={true}
                            depthWrite={false}
                            depthTest={false}
                          />
                        </mesh>
                      </>
                    )}
                  </group>
                )}
              </>
            )}
          </group>
        ))}

        {/* Back Cover */}
        <mesh position={[0, 0, 0.05 - ((pages.length + 1) * 0.01)]} renderOrder={0}>
          <planeGeometry args={[4.2, 6.2]} />
          <meshStandardMaterial 
            map={backTexture}
            side={THREE.DoubleSide}
            transparent={true}
            depthWrite={true}
            depthTest={true}
          />
        </mesh>
      </group>

      {/* Navigation buttons */}
      <Html
        position={[isVideoPage ? -1.0 : -2, isVideoPage ? -1.05 : -2, 0]}  // Moved up to -1.05 and left to -1.0
        style={{
          transition: 'all 0.5s ease',
          transform: `scale(${isVideoPage ? 1.2 : 1})`
        }}
      >
        <button
          className="omori-button"
          onClick={handlePrevPage}
          disabled={currentPage === 0 || isFlipping}
        >
          Previous Page
        </button>
      </Html>

      <Html
        position={[isVideoPage ? 0.8 : 2, isVideoPage ? -1.05 : -2, 0]}  // Just moved up to -1.05
        style={{
          transition: 'all 0.5s ease',
          transform: `scale(${isVideoPage ? 1.2 : 1})`
        }}
      >
        <button
          className="omori-button"
          onClick={handleNextPage}
          disabled={currentPage === pages.length - 1 || isFlipping}
        >
          Next Page
        </button>
      </Html>
    </group>
  );
};

const ParticleSystem = () => {
  return (
    <Stars 
      radius={100}
      depth={50}
      count={5000}
      factor={4}
      saturation={0}
      fade
    />
  );
};

const PhotoAlbum = ({ backgroundMusicRef, onVideoStateChange }) => {
  return (
    <Frame
      boxShadow="in"
      style={{
        width: '60vh',
        height: '60vh',
        margin: '0 auto',
        overflow: 'hidden',
        border: '2px solid #000',
      }}
    >
      <Canvas style={{ width: '100%', height: '100%', background: 'black' }}>
        <OrbitControls />
        <Suspense fallback={null}>
          <ambientLight intensity={0.3} />
          <pointLight position={[10, 10, 10]} intensity={0.5} />
          <spotLight 
            position={[5, 10, 5]} 
            angle={0.3} 
            penumbra={1} 
            intensity={1} 
            castShadow 
          />
          <ParticleSystem />
          <Book backgroundMusicRef={backgroundMusicRef} onVideoStateChange={onVideoStateChange} />
        </Suspense>
      </Canvas>
    </Frame>
  );
};

export default PhotoAlbum; 